import { DefaultTheme, ThemeProvider } from '@react-navigation/native';
import { useFonts } from 'expo-font';
import { Stack } from 'expo-router';
import { useEffect } from 'react';
import { Platform } from 'react-native';
import 'react-native-reanimated';
import { SafeAreaProvider } from 'react-native-safe-area-context';

import { ContentfulForegroundRefresh } from '@/core/components/ContentfulForegroundRefresh';
import { GlobalErrorHandler } from '@/core/components/GlobalErrorHandler';
import { GlobalWebStyles } from '@/core/components/GlobalWebStyles';
import { WebPathDocumentTitle } from '@/core/components/WebDocumentHead';
import { AuthProvider } from '@/core/contexts/AuthContext';
import { useUIColors, useTranslation } from '@/core/hooks';
import { logger } from '@/core/logger';
import { appVersion } from '@/core/wrappers/appVersion';
// import { useColorScheme } from '@/core/hooks/useColorScheme'; // Disabled - themes are not used
import { I18nProvider } from '@/core/providers/I18nProvider';
import { WebViewportProvider } from '@/core/providers/WebViewportProvider';
import { QueryProvider } from '@/core/providers/QueryProvider';
import { branchService } from '@/core/services/branchService';
import { initDynatrace } from '@/core/services/dynatraceService';
import { Toast } from '@/components/molecules/Toast';
import { useAppStore } from '@/core/stores/appStore';

function AppContent() {
  const colors = useUIColors();
  const logoutToastVisible = useAppStore((state) => state.logoutToastVisible);
  const setLogoutToastVisible = useAppStore((state) => state.setLogoutToastVisible);
  const { t } = useTranslation();

  return (
    <>
      <WebPathDocumentTitle />
      <Stack
      screenOptions={{
        headerShown: false,
        contentStyle: { backgroundColor: colors.backgroundLevel1 },
        statusBarStyle: 'dark',
      }}
    >
      {/* Public routes */}
      <Stack.Screen name="index" />
      <Stack.Screen name="welcome" options={{ statusBarStyle: 'light' }} />
      <Stack.Screen name="login" />
      <Stack.Screen name="logout-success" />
      <Stack.Screen name="forgot-password" />
      <Stack.Screen name="uae-pass-details" />
      <Stack.Screen name="uae-pass" />
      <Stack.Screen name="uaePassSuccess" />
      <Stack.Screen name="uaePassFail" />
      <Stack.Screen name="uaepass" />
      <Stack.Screen name="phone-otp" />
      <Stack.Screen name="email-otp" />
      <Stack.Screen name="registration-success" />
      <Stack.Screen name="passcode" />
      {/* Manual registration routes - only on native platforms (not web) */}
      {Platform.OS !== 'web' && (
        <>
          <Stack.Screen name="registration-form" />
          <Stack.Screen name="registration-success-manual" />
          <Stack.Screen name="document-scanner" />
          <Stack.Screen name="document-review" />
          <Stack.Screen name="phone-otp-inline" />
          <Stack.Screen name="email-otp-inline" />
        </>
      )}
      <Stack.Screen name="password-sent" />
      <Stack.Screen name="personal-details" />
      <Stack.Screen name="id-proof" />
      <Stack.Screen name="faq" />
      <Stack.Screen name="venture" />
      {/* Public guest catalogue (web) — flat routes so static export is .html, not guest/ */}
      <Stack.Screen name="guest" options={{ headerShown: false }} />
      <Stack.Screen name="guest-details" options={{ headerShown: false }} />
      {/* Public guest home (mobile) — temporary, reached from login's "Continue as Guest" */}
      <Stack.Screen name="guest-home" options={{ headerShown: false }} />
      <Stack.Screen name="guest-home-details" options={{ headerShown: false }} />
      <Stack.Screen name="universal" options={{ headerShown: false, statusBarStyle: 'light' }} />
      <Stack.Screen name="universal-space" options={{ headerShown: false }} />
      {/* Legacy aliases → Redirect in route files */}
      <Stack.Screen name="bloom-universal" options={{ headerShown: false, statusBarStyle: 'light' }} />
      <Stack.Screen name="bloom-universal-space" options={{ headerShown: false }} />
      {/* Catch-all route for unmatched routes - automatically handled by [...missing].tsx */}

      {/* Protected routes */}
      <Stack.Screen
        name="(auth)"
        options={{ headerShown: false, gestureEnabled: false }}
      />
    </Stack>
    {Platform.OS === 'web' && (
      <Toast 
        visible={logoutToastVisible} 
        message={t('profile.loggedOutSuccessfully') || 'Logged out successfully'} 
        onHide={() => setLogoutToastVisible(false)} 
        position="top-right"
      />
    )}
    </>
  );
}

export default function RootLayout() {
  // NOTE: Theme switching is currently disabled
  // We are not using themes in our app at this time, so system theme changes
  // will not affect the app. The app will always use the light theme (DefaultTheme).
  // In the future, when we implement theme support, we will enable this functionality.
  // const colorScheme = useColorScheme(); // Disabled - always use light theme
  const [loaded] = useFonts({
    // Back-compat alias
    Matter: require('../assets/fonts/Matter-Regular.otf'),
    // Full family
    'Matter-Regular': require('../assets/fonts/Matter-Regular.otf'),
    'Matter-RegularItalic': require('../assets/fonts/Matter-RegularItalic.otf'),
    'Matter-Medium': require('../assets/fonts/Matter-Medium.otf'),
    'Matter-MediumItalic': require('../assets/fonts/Matter-MediumItalic.otf'),
    'Matter-SemiBold': require('../assets/fonts/Matter-SemiBold.otf'),
    'Matter-SemiBoldItalic': require('../assets/fonts/Matter-SemiBoldItalic.otf'),
    'Matter-Bold': require('../assets/fonts/Matter-Bold.otf'),
    'Matter-BoldItalic': require('../assets/fonts/Matter-BoldItalic.otf'),
    'Matter-Light': require('../assets/fonts/Matter-Light.otf'),
    'Matter-LightItalic': require('../assets/fonts/Matter-LightItalic.otf'),
    'Matter-Heavy': require('../assets/fonts/Matter-Heavy.otf'),
    'Matter-HeavyItalic': require('../assets/fonts/Matter-HeavyItalic.otf'),
    // Marketing serif for web hero
    'Orpheus-Pro': require('../assets/fonts/Orpheus-Pro.otf'),
  });

  // Branch (deep links only — see branchService) and Dynatrace (first-party RUM).
  // No App Tracking Transparency: we do not use IDFA or cross-app tracking.
  useEffect(() => {
    const version = appVersion.getBuildVersion();
    logger.info('AppStartup', 'Build version', {
      version: version ?? 'unknown',
      platform: Platform.OS,
    });

    if (Platform.OS !== 'web') {
      branchService.initialize().catch((error) => {
        logger.warn('AppStartup', 'Failed to initialize Branch', { error });
      });
    }

    initDynatrace().catch((error) => {
      logger.warn('AppStartup', 'Failed to initialize Dynatrace', { error });
    });

    // Cleanup on unmount
    return () => {
      if (Platform.OS !== 'web') {
        branchService.cleanup();
      }
    };
  }, []);

  // Web-only: expose build version via HTML metadata for easy identification.
  useEffect(() => {
    if (Platform.OS !== 'web') return;
    if (typeof document === 'undefined') return;

    const version = appVersion.getBuildVersion();
    if (!version) return;

    const metaName = 'bloom-build-version';
    let meta = document.querySelector(
      `meta[name="${metaName}"]`,
    ) as HTMLMetaElement | null;

    if (!meta) {
      meta = document.createElement('meta');
      meta.name = metaName;
      document.head.appendChild(meta);
    }

    meta.content = version;

    // Optional: also expose as data attribute for quick inspection.
    document.documentElement.dataset.buildVersion = version;
  }, []);

  if (!loaded) {
    // Async font loading only occurs in development.
    return null;
  }

  return (
    <SafeAreaProvider>
      <WebViewportProvider>
        <I18nProvider>
          <QueryProvider>
            <ThemeProvider value={DefaultTheme}>
              <AuthProvider>
                <AppContent />
                <GlobalErrorHandler />
                <ContentfulForegroundRefresh />
                {Platform.OS === 'web' && <GlobalWebStyles />}
              </AuthProvider>
            </ThemeProvider>
          </QueryProvider>
        </I18nProvider>
      </WebViewportProvider>
    </SafeAreaProvider>
  );
}
